// Resolve open-letter items (stored by Bot-API id) to the user's MTProto DM
// messages. The two id spaces are per-account counters with no constant offset
// (see CLAUDE.md "Why id arithmetic is unsolvable"), so we match by send date +
// order. Pure: takes plain data, returns the mapping. Tested in resolve.test.ts.

// LetterItem is the API contract — generated from the Python schema.
export type { LetterItem } from "./api-types.js";
import type { LetterItem } from "./api-types.js";

/** A DM message as seen by the user's GramJS session (minimal shape). */
export interface DmMessage {
  id: number;
  /** Unix seconds. */
  date: number;
  /** True for messages the user sent (letter content). */
  out: boolean;
}

export interface ResolveResult {
  /** Item dm_message_id -> the MTProto message it maps to (in input order).
   *  An item with no match is omitted. */
  matched: { item: LetterItem; mtproto_id: number }[];
  /** Newly-resolved ids to freeze server-side: {dm_message_id -> mtproto_id}. */
  toFreeze: Record<string, number>;
}

/** Tolerance (seconds) between an item's stored date and the DM message date.
 *  The bot and the client both read Telegram's authoritative send time, so this
 *  is small; it only absorbs rounding. */
const DATE_TOLERANCE_S = 2;

/**
 * Map each item to its MTProto message.
 *
 * - Frozen items (mtproto_id set) render by exact id; if that id is gone from
 *   history the user deleted it in Telegram, so the item is omitted (never
 *   re-matched — that would mis-bind).
 * - Unfrozen items with a date are matched ONCE by a monotonic walk of the
 *   user's own (out) messages, on date (±tolerance) + send-order, then returned
 *   in `toFreeze` to be cached server-side.
 * - Items with no date are legacy (pre-date column): no reliable signal, skipped.
 *
 * An MTProto id is never bound to two items, even on a same-second tie.
 */
export function resolveLetterItems(
  items: LetterItem[],
  dmMessages: DmMessage[]
): ResolveResult {
  const byId = new Map<number, DmMessage>();
  const outMsgs: DmMessage[] = [];
  for (const m of dmMessages) {
    byId.set(m.id, m);
    if (m.out) outMsgs.push(m);
  }
  outMsgs.sort((a, b) => a.id - b.id); // chronological (id order == send order)

  // mt ids already bound (frozen, or matched this pass) — never bind twice.
  const usedMt = new Set<number>(
    items.map((it) => it.mtproto_id).filter((x): x is number => x != null)
  );

  let cursor = 0; // monotonic pointer into outMsgs
  const resolveByMatch = (date: number): DmMessage | null => {
    for (let k = cursor; k < outMsgs.length; k++) {
      const md = outMsgs[k].date;
      if (md > date + DATE_TOLERANCE_S) break; // past the window, no match
      if (Math.abs(md - date) > DATE_TOLERANCE_S || usedMt.has(outMsgs[k].id)) {
        continue;
      }
      cursor = k + 1;
      usedMt.add(outMsgs[k].id);
      return outMsgs[k];
    }
    return null;
  };

  const matched: { item: LetterItem; mtproto_id: number }[] = [];
  const toFreeze: Record<string, number> = {};
  for (const it of items) {
    let m: DmMessage | null = null;
    if (it.mtproto_id != null) {
      m = byId.get(it.mtproto_id) ?? null;
    } else if (it.date != null) {
      m = resolveByMatch(it.date);
      if (m) toFreeze[String(it.dm_message_id)] = m.id;
    }
    if (m) matched.push({ item: it, mtproto_id: m.id });
  }
  return { matched, toFreeze };
}
